
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SwapContextType, SwapState, SwapTokenInfo } from '../types/swap';
import { JupiterQuoteResponse } from '../lib/solana/jupiter';
import { useWallet } from '../providers/WalletProvider';
import { jupiterApi } from '../api/jupiter';
import { toBaseUnit, fromBaseUnit } from '../lib/utils/format';
import { SOL_MINT } from '../lib/solana/solana';

const SwapContext = createContext<SwapContextType | undefined>(undefined);

export const useSwap = () => {
    const context = useContext(SwapContext);
    if (!context) {
        throw new Error('useSwap must be used within a SwapProvider');
    }
    return context;
};

export const SwapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isConnected, address, chain, balance, refreshBalance } = useWallet();

    const [swapState, setSwapState] = useState<SwapState>({
        inputToken: null,
        outputToken: null,
        inputAmount: '',
        outputAmount: '',
        slippage: 0.5,
        isBuy: true
    });

    const [quote, setQuote] = useState<JupiterQuoteResponse | null>(null);
    const [isQuoting, setIsQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState<string | null>(null);

    const [isSwapping, setIsSwapping] = useState(false);
    const [swapError, setSwapError] = useState<string | null>(null);
    const [txHash, setTxHash] = useState<string | null>(null);

    // Update isBuy based on tokens
    useEffect(() => {
        if (swapState.inputToken && swapState.outputToken) {
            // If Input is SOL/USDC, it's a BUY
            const isInputStable = swapState.inputToken.symbol === 'SOL' || swapState.inputToken.symbol === 'USDC';
            setSwapState(prev => ({ ...prev, isBuy: isInputStable }));
        }
    }, [swapState.inputToken?.symbol]);

    // Fetch Quote Debounced
    useEffect(() => {
        const fetchQuote = async () => {
            if (!swapState.inputToken || !swapState.outputToken || !swapState.inputAmount || Number(swapState.inputAmount) <= 0) {
                setQuote(null);
                setSwapState(prev => ({ ...prev, outputAmount: '' }));
                return;
            }

            // Only quote if valid addresses
            if (!swapState.inputToken.address || !swapState.outputToken.address) return;

            setIsQuoting(true);
            setQuoteError(null);

            try {
                const amountInAtomic = Number(toBaseUnit(Number(swapState.inputAmount), swapState.inputToken.decimals));
                
                const response = await jupiterApi.quote({
                    inputMint: swapState.inputToken.address,
                    outputMint: swapState.outputToken.address,
                    amount: amountInAtomic,
                    slippageBps: swapState.slippage * 100
                });

                setQuote(response);
                
                // Calculate output display
                const outAmount = fromBaseUnit(response.outAmount, swapState.outputToken.decimals);
                setSwapState(prev => ({ ...prev, outputAmount: outAmount.toFixed(4) }));

            } catch (err: any) {
                console.error(err);
                setQuoteError("Failed to fetch quote.");
                setQuote(null);
            } finally {
                setIsQuoting(false);
            }
        };

        const timeout = setTimeout(fetchQuote, 600);
        return () => clearTimeout(timeout);
    }, [swapState.inputAmount, swapState.inputToken, swapState.outputToken, swapState.slippage]);

    const executeSwap = async (): Promise<string | null> => {
        if (!isConnected || !address) {
            setSwapError("Wallet not connected");
            return null;
        }
        if (!quote) {
            setSwapError("No valid quote found");
            return null;
        }

        setIsSwapping(true);
        setSwapError(null);
        setTxHash(null);

        try {
            // 1. Get Transaction Payload
            const { swapTransaction } = await jupiterApi.swap(quote, address);
            
            // 2. Simulate Sign & Send
            // In real app: const txid = await wallet.sendTransaction(swapTransaction, connection);
            await new Promise(r => setTimeout(r, 2000));
            
            // Mock Success
            const mockTxHash = `5${Math.random().toString(36).slice(2, 60)}`;
            setTxHash(mockTxHash);
            
            // Refresh balances
            await refreshBalance();
            
            setIsSwapping(false);
            return mockTxHash;

        } catch (err: any) {
            console.error(err);
            setSwapError("Swap failed: " + (err.message || "Unknown error"));
            setIsSwapping(false);
            return null;
        }
    };

    const switchTokens = () => {
        setSwapState(prev => ({
            ...prev,
            inputToken: prev.outputToken,
            outputToken: prev.inputToken,
            inputAmount: prev.outputAmount, // simple swap of values
            outputAmount: '', // Trigger requote
            isBuy: !prev.isBuy
        }));
    };

    const reset = () => {
        setSwapState(prev => ({ ...prev, inputAmount: '', outputAmount: '' }));
        setQuote(null);
        setTxHash(null);
        setSwapError(null);
    };

    const updateSwapState = (updates: Partial<SwapState>) => {
        setSwapState(prev => ({ ...prev, ...updates }));
    };

    return (
        <SwapContext.Provider value={{
            swapState,
            setSwapState: updateSwapState,
            quote,
            isQuoting,
            quoteError,
            executeSwap,
            isSwapping,
            swapError,
            txHash,
            switchTokens,
            reset
        }}>
            {children}
        </SwapContext.Provider>
    );
};
